// src/components/Common/ErrorMessage.tsx

import React from 'react';
import { AlertCircle, RefreshCw, XCircle, WifiOff, AlertTriangle } from 'lucide-react';

interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
  type?: 'error' | 'warning' | 'network';
  details?: string;
  className?: string;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ 
  message, 
  onRetry, 
  type = 'error',
  details,
  className = ''
}) => {
  const getErrorIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'network':
        return <WifiOff className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getErrorStyles = () => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'network':
        return 'bg-gray-50 border-gray-200 text-gray-700';
      default:
        return 'bg-red-50 border-red-200 text-red-700';
    }
  };

  const getButtonStyles = () => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700';
      case 'network':
        return 'bg-gray-600 hover:bg-gray-700';
      default:
        return 'bg-red-600 hover:bg-red-700';
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center py-8 px-4 ${className}`}>
      <div className={`w-full max-w-md p-4 rounded-lg border ${getErrorStyles()}`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {getErrorIcon()}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-sm">
              {type === 'network' ? 'Connection Error' : 'Error'}
            </h3>
            <p className="mt-1 text-sm">
              {message}
            </p>
            {details && (
              <p className="mt-2 text-xs opacity-75">
                {details}
              </p>
            )}
          </div>
        </div>
        
        {onRetry && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={onRetry}
              className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg transition ${getButtonStyles()}`}
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Alternative inline error message for forms
export const InlineError: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
      <XCircle className="w-4 h-4" />
      <span>{message}</span>
    </div>
  );
};

// Error boundary component for catching React errors
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                Something went wrong
              </h1>
            </div>
            
            <p className="text-gray-600 mb-4">
              We encountered an unexpected error. Please refresh the page or try again later.
            </p>
            
            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error details
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Toast notification for temporary errors
export const ErrorToast: React.FC<{
  message: string;
  onClose: () => void;
  duration?: number;
}> = ({ message, onClose, duration = 5000 }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
      <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm">{message}</p>
        <button
          onClick={onClose}
          className="ml-auto p-1 hover:bg-red-700 rounded"
        >
          <XCircle className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Empty state component for when there's no data
export const EmptyState: React.FC<{
  title: string;
  message: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}> = ({ title, message, icon, action }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {icon && (
        <div className="mb-4 p-4 bg-gray-100 rounded-full">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-gray-600 text-center max-w-sm">
        {message}
      </p>
      {action && (
        <button
          onClick={action.onClick}
          className="tl-btn tl-btn-primary mt-4"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

// Default export
export default ErrorMessage;